/**
 * Zentrale Intl-Formatter-Helfer.
 * Ausschließlich Intl-API – keine hart kodierten Formatstrings.
 * Sprachumgebung: 'de-DE' (Portal ist bewusst DE-only).
 * Struktur ist sprachgenerisch, damit späteres Multi-Locale kein Rewrite erfordert.
 */

const LOCALE = 'de-DE';

// ─── Datum & Zeit ──────────────────────────────────────────────────────────────

/** "17. Apr. 2026" */
export function formatDate(value: Date | string | null | undefined): string {
  if (!value) return '–';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}

/** "17.04.2026" */
export function formatDateShort(value: Date | string | null | undefined): string {
  if (!value) return '–';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

/** "17. Apr. 2026, 09:30 Uhr" */
export function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return '–';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** "17.04.26, 09:30" – kompakt, 2-stelliges Jahr; gibt null bei ungültigem Wert zurück (für bedingtes Rendern) */
export function formatDateTimeShort(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** "09:30" – nur die Uhrzeit */
export function formatTime(value: Date | string | null | undefined): string {
  if (!value) return '–';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit' }).format(d);
}

/** "vor 3 Stunden", "in 2 Tagen" */
export function formatRelativeTime(value: Date | string | null | undefined): string {
  if (!value) return '–';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '–';

  const diffMs = d.getTime() - Date.now();
  const diffSec = Math.round(diffMs / 1000);
  const absS = Math.abs(diffSec);

  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

  if (absS < 60) return rtf.format(diffSec, 'second');
  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
  const diffH = Math.round(diffMin / 60);
  if (Math.abs(diffH) < 24) return rtf.format(diffH, 'hour');
  const diffD = Math.round(diffH / 24);
  if (Math.abs(diffD) < 30) return rtf.format(diffD, 'day');
  const diffW = Math.round(diffD / 7);
  if (Math.abs(diffW) < 8) return rtf.format(diffW, 'week');
  const diffM = Math.round(diffD / 30);
  if (Math.abs(diffM) < 12) return rtf.format(diffM, 'month');
  return rtf.format(Math.round(diffD / 365), 'year');
}

// ─── Zahlen & Währung ─────────────────────────────────────────────────────────

/** "1.234,56 €" */
export function formatCurrency(
  value: number | null | undefined,
  currency = 'EUR'
): string {
  if (value == null) return '–';
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** "1.234,5" */
export function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
  if (value == null) return '–';
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** "75 %" */
export function formatPercent(value: number | null | undefined): string {
  if (value == null) return '–';
  return new Intl.NumberFormat(LOCALE, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value / 100);
}
